import { BehaviorSubject, Observable } from 'rxjs';
import {
    createShoppingListMutation,
    deleteShoppingListMutation,
    getProductsInShoppingListQuery,
    getCustomersInShoppingListQuery,
    getShoppingListByIdQuery,
    getShoppingListsQuery,
    updateShoppingListMutation,
    importCustomersToShoppingListMutation,
    importProductsToShoppingListMutation,
    importPromotionsToShoppingListMutation,
    importProductsByBrandToShoppingListMutation,
    getShoppingListBrandImportProductsQuery,
    searchShoppingListImportBrandsQuery,
    removeCustomersFromShoppingListMutation,
    removeBuyersFromShoppingListMutation,
    removeItemsFromShoppingListMutation,
    removePromotionFromShoppingListMutation,
    assignShoppingListToChannelsMutation,
    removeChannelFromShoppingListMutation,
    generateShoppingListCustomerAccessUrlMutation,
    getShoppingListCommunicationBatchesQuery,
    getShoppingListCommunicationRecipientsQuery,
    sendShoppingListCommunicationMutation,
    downloadTemplateUrlQuery,
    customerSegmentsQuery,
    downloadProductsTemplateUrlQuery,
    getChannelsQuery,
    getShoppingListPromotionImportOptionsQuery,
    getShoppingListPromotionImportPreviewQuery,
    getShoppingListPromotionsQuery,
    getShoppingListPromotionSharingEligibleCustomersQuery,
    previewShoppingListPromotionCommunicationMutation,
    sendShoppingListPromotionCommunicationMutation,
    shareShoppingListPromotionByWhatsAppWebMutation,
} from './queries';

import { Apollo } from 'apollo-angular';
import { Injectable } from '@angular/core';
import { addBulkItemsToShoppingListMutation } from './components/assign-products-to-shopping-list-dialog/assign-products-to-shopping-list-dialog.graphql';
import { associateAllBuyersWithShoppingList } from './components/assign-customer-to-shopping-list-dialog/assign-customer-to-shopping-list-dialog.graphql';
import { map } from 'rxjs/operators';

@Injectable({
    providedIn: 'root',
})
export class ShoppingListService {
    private shoppingListsSubject = new BehaviorSubject<any[]>([]);
    private totalItemsShoppingListSubject = new BehaviorSubject<number>(0);
    shoppingLists$ = this.shoppingListsSubject.asObservable();
    totalItemsShoppingList$ = this.totalItemsShoppingListSubject.asObservable();

    constructor(private apollo: Apollo) {}

    loadShoppingLists(
        skip: number = 0,
        take: number = 10,
        searchTerm: string = ''
    ): void {
        const filter = searchTerm ? { name: { contains: searchTerm } } : {};
        this.apollo
            .watchQuery({
                query: getShoppingListsQuery,
                variables: { skip, take, filter },
                fetchPolicy: 'network-only',
            })
            .valueChanges.pipe(
                map((result: any) => result.data.getShoppingLists)
            )
            .subscribe((data) => {
                this.shoppingListsSubject.next(data.items);
                this.totalItemsShoppingListSubject.next(data.totalItems);
            });
    }

    searchShoppingLists(
        searchTerm: string,
        skip: number = 0,
        take: number = 10
    ): Observable<{ items: any[]; totalItems: number }> {
        return this.apollo
            .watchQuery({
                query: getShoppingListsQuery,
                variables: {
                    skip,
                    take,
                    filter: { name: { contains: searchTerm } },
                },
                fetchPolicy: 'network-only',
            })
            .valueChanges.pipe(
                map((result: any) => ({
                    items: result.data.getShoppingLists.items,
                    totalItems: result.data.getShoppingLists.totalItems,
                }))
            );
    }

    getShoppingListById(id: string): Observable<any> {
        return this.apollo
            .query({
                query: getShoppingListByIdQuery,
                variables: { id },
                fetchPolicy: 'network-only',
            })
            .pipe(
                map((result: any) => {
                    return result.data.getShoppingList;
                })
            );
    }

    getCustomersInShoppingList(
        shoppingListId: string,
        skip: number = 0,
        take: number = 10,
        document: string = ''
    ): Observable<any> {
        return this.apollo
            .query({
                query: getCustomersInShoppingListQuery,
                variables: {
                    shoppingListId,
                    skip,
                    take,
                    document,
                },
                fetchPolicy: 'no-cache',
            })
            .pipe(
                map((result: any) => {
                    return result.data.getCustomersInShoppingList;
                })
            );
    }

    getProductsInShoppingList(
        shoppingListId: string,
        skip: number = 0,
        take: number = 10,
        promotionId?: string | null
    ): Observable<any> {
        return this.apollo
            .query({
                query: getProductsInShoppingListQuery,
                variables: {
                    shoppingListId,
                    skip,
                    take,
                    promotionId: promotionId || null,
                },
                fetchPolicy: 'no-cache',
            })
            .pipe(
                map((result: any) => {
                    return result.data.getProductsInShoppingList;
                })
            );
    }

    getShoppingListPromotions(shoppingListId: string): Observable<any[]> {
        return this.apollo
            .query({
                query: getShoppingListPromotionsQuery,
                variables: { shoppingListId },
                fetchPolicy: 'no-cache',
            })
            .pipe(
                map(
                    (result: any) => result.data.getShoppingListPromotions || []
                )
            );
    }

    getPromotionSharingEligibleCustomers(
        shoppingListId: string,
        promotionId: string,
        document: string = '',
        skip: number = 0,
        take: number = 10
    ): Observable<any> {
        return this.apollo
            .query({
                query: getShoppingListPromotionSharingEligibleCustomersQuery,
                variables: {
                    shoppingListId,
                    promotionId,
                    document,
                    skip,
                    take,
                },
                fetchPolicy: 'no-cache',
            })
            .pipe(
                map(
                    (result: any) =>
                        result.data
                            .getShoppingListPromotionSharingEligibleCustomers
                )
            );
    }

    getPromotionImportOptions(
        shoppingListId: string,
        searchTerm: string = '',
        skip: number = 0,
        take: number = 20
    ): Observable<any> {
        return this.apollo
            .query({
                query: getShoppingListPromotionImportOptionsQuery,
                variables: {
                    shoppingListId,
                    searchTerm,
                    skip,
                    take,
                },
                fetchPolicy: 'no-cache',
            })
            .pipe(
                map(
                    (result: any) =>
                        result.data.getShoppingListPromotionImportOptions
                )
            );
    }

    getPromotionImportPreview(
        shoppingListId: string,
        promotionIds: string[]
    ): Observable<any> {
        return this.apollo
            .query({
                query: getShoppingListPromotionImportPreviewQuery,
                variables: {
                    shoppingListId,
                    promotionIds,
                },
                fetchPolicy: 'no-cache',
            })
            .pipe(
                map(
                    (result: any) =>
                        result.data.getShoppingListPromotionImportPreview
                )
            );
    }

    importPromotionsToShoppingList(
        shoppingListId: string,
        promotionIds: string[]
    ): Observable<any> {
        return this.apollo
            .mutate({
                mutation: importPromotionsToShoppingListMutation,
                variables: {
                    shoppingListId,
                    promotionIds,
                },
            })
            .pipe(
                map((result: any) => result.data.importPromotionsToShoppingList)
            );
    }

    createShoppingList(input: any) {
        const {
            name,
            description,
            imageId,
            startDate,
            endDate,
            enabled,
            isPrivate,
            originType,
            assignmentType,
            segmentIds,
            channelIds,
        } = input;
        return this.apollo
            .mutate({
                mutation: createShoppingListMutation,
                variables: {
                    input: {
                        name,
                        description,
                        imageId,
                        startDate,
                        endDate,
                        enabled,
                        isPrivate,
                        originType,
                        assignmentType,
                        segmentIds,
                        channelIds,
                    },
                },
                refetchQueries: [{ query: getShoppingListsQuery }],
            })
            .pipe(map((result: any) => result.data.createShoppingList));
    }

    updateShoppingList(input: any) {
        return this.apollo.mutate({
            mutation: updateShoppingListMutation,
            variables: { input },
            refetchQueries: [{ query: getShoppingListsQuery }],
        });
    }

    addBulkItemsToShoppingList(input: any) {
        return this.apollo
            .mutate({
                mutation: addBulkItemsToShoppingListMutation,
                variables: { input },
                refetchQueries: [{ query: getShoppingListsQuery }],
            })
            .pipe(map((result: any) => result.data.addBulkItemsToShoppingList));
    }

    associateAllBuyersWithShoppingList(input: any) {
        return this.apollo
            .mutate({
                mutation: associateAllBuyersWithShoppingList,
                variables: { input },
                refetchQueries: [{ query: getShoppingListsQuery }],
            })
            .pipe(
                map(
                    (result: any) =>
                        result.data.associateAllBuyersWithShoppingList
                )
            );
    }

    deleteShoppingLists(shoppingListIds: string[]): Observable<void> {
        return this.apollo
            .mutate({
                mutation: deleteShoppingListMutation,
                variables: { ids: shoppingListIds },
                refetchQueries: [{ query: getShoppingListsQuery }],
            })
            .pipe(
                map(() => {
                    console.log('Service: Shopping Lists deleted successfully');
                })
            );
    }

    sendCustomersFile(shoppingListId: string, file: File) {
        return this.apollo
            .mutate({
                mutation: importCustomersToShoppingListMutation,
                variables: {
                    shoppingListId,
                    file,
                },
                context: {
                    useMultipart: true,
                },
            })
            .pipe(
                map((result: any) => result.data.importCustomersToShoppingList)
            );
    }

    sendProductsFile(shoppingListId: string, file: File) {
        return this.apollo
            .mutate({
                mutation: importProductsToShoppingListMutation,
                variables: {
                    shoppingListId,
                    file,
                },
                context: {
                    useMultipart: true,
                },
            })
            .pipe(
                map((result: any) => result.data.importProductsToShoppingList)
            );
    }

    searchShoppingListImportBrands(
        shoppingListId: string,
        term: string = '',
        skip: number = 0,
        take: number = 20
    ) {
        return this.apollo
            .query({
                query: searchShoppingListImportBrandsQuery,
                variables: {
                    input: {
                        shoppingListId,
                        term,
                        skip,
                        take,
                    },
                },
                fetchPolicy: 'no-cache',
            })
            .pipe(
                map((result: any) => result.data.searchShoppingListImportBrands)
            );
    }

    getShoppingListBrandImportProducts(
        shoppingListId: string,
        brandId: string,
        skip: number = 0,
        take: number = 10
    ) {
        return this.apollo
            .query({
                query: getShoppingListBrandImportProductsQuery,
                variables: {
                    input: {
                        shoppingListId,
                        brandId,
                        skip,
                        take,
                    },
                },
                fetchPolicy: 'no-cache',
            })
            .pipe(
                map(
                    (result: any) =>
                        result.data.getShoppingListBrandImportProducts
                )
            );
    }

    importProductsByBrandToShoppingList(input: {
        shoppingListId: string;
        brandId: string;
        mode: 'selected' | 'all';
        productVariantIds?: string[];
    }) {
        return this.apollo
            .mutate({
                mutation: importProductsByBrandToShoppingListMutation,
                variables: { input },
            })
            .pipe(
                map(
                    (result: any) =>
                        result.data.importProductsByBrandToShoppingList
                )
            );
    }

    removeAllBuyersFromShoppingList(shoppingListId: string) {
        return this.apollo
            .mutate({
                mutation: removeCustomersFromShoppingListMutation,
                variables: {
                    shoppingListId,
                },
            })
            .pipe(
                map(
                    (result: any) => result.data.removeCustomersFromShoppingList
                )
            );
    }

    removeBuyersFromShoppingList(
        shoppingListId: string,
        customerIds: string[]
    ) {
        return this.apollo
            .mutate({
                mutation: removeBuyersFromShoppingListMutation,
                variables: {
                    shoppingListId,
                    customerIds,
                },
            })
            .pipe(
                map((result: any) => result.data.removeBuyersFromShoppingList)
            );
    }

    removeItemsFromShoppingList(shoppingListId: string, itemIds: string[]) {
        return this.apollo
            .mutate({
                mutation: removeItemsFromShoppingListMutation,
                variables: {
                    shoppingListId,
                    itemIds,
                },
            })
            .pipe(
                map((result: any) => result.data.removeItemsFromShoppingList)
            );
    }

    removePromotionFromShoppingList(
        shoppingListId: string,
        promotionId: string
    ) {
        return this.apollo
            .mutate({
                mutation: removePromotionFromShoppingListMutation,
                variables: {
                    shoppingListId,
                    promotionId,
                },
            })
            .pipe(
                map(
                    (result: any) => result.data.removePromotionFromShoppingList
                )
            );
    }

    generateShoppingListCustomerAccessUrl(
        shoppingListId: string,
        customerId: string
    ) {
        return this.apollo
            .mutate({
                mutation: generateShoppingListCustomerAccessUrlMutation,
                variables: {
                    shoppingListId,
                    customerId,
                },
            })
            .pipe(
                map(
                    (result: any) =>
                        result.data.generateShoppingListCustomerAccessUrl
                )
            );
    }

    sendShoppingListCommunication(
        shoppingListId: string,
        customerIds: string[],
        channel: 'email' | 'whatsapp' = 'email',
        selectionMode:
            | 'selected_customers'
            | 'all_eligible_customers' = 'selected_customers'
    ) {
        return this.apollo
            .mutate({
                mutation: sendShoppingListCommunicationMutation,
                variables: {
                    shoppingListId,
                    customerIds,
                    channel,
                    selectionMode,
                },
            })
            .pipe(
                map((result: any) => result.data.sendShoppingListCommunication)
            );
    }

    previewShoppingListPromotionCommunication(
        shoppingListId: string,
        channel: 'email' | 'whatsapp' | 'whatsapp_web',
        assignments: Array<{ customerId: string; promotionId: string }>
    ): Observable<any> {
        return this.apollo
            .mutate({
                mutation: previewShoppingListPromotionCommunicationMutation,
                variables: {
                    shoppingListId,
                    channel,
                    assignments,
                },
            })
            .pipe(
                map(
                    (result: any) =>
                        result.data.previewShoppingListPromotionCommunication
                )
            );
    }

    sendShoppingListPromotionCommunication(
        shoppingListId: string,
        channel: 'email' | 'whatsapp',
        assignments: Array<{ customerId: string; promotionId: string }>
    ): Observable<any> {
        return this.apollo
            .mutate({
                mutation: sendShoppingListPromotionCommunicationMutation,
                variables: {
                    shoppingListId,
                    channel,
                    assignments,
                },
            })
            .pipe(
                map(
                    (result: any) =>
                        result.data.sendShoppingListPromotionCommunication
                )
            );
    }

    shareShoppingListPromotionByWhatsAppWeb(
        shoppingListId: string,
        customerId: string,
        promotionId: string
    ): Observable<any> {
        return this.apollo
            .mutate({
                mutation: shareShoppingListPromotionByWhatsAppWebMutation,
                variables: {
                    shoppingListId,
                    customerId,
                    promotionId,
                },
            })
            .pipe(
                map(
                    (result: any) =>
                        result.data.shareShoppingListPromotionByWhatsAppWeb
                )
            );
    }

    getShoppingListCommunicationBatches(
        shoppingListId: string,
        skip: number = 0,
        take: number = 10
    ): Observable<any> {
        return this.apollo
            .query({
                query: getShoppingListCommunicationBatchesQuery,
                variables: {
                    shoppingListId,
                    skip,
                    take,
                },
                fetchPolicy: 'no-cache',
            })
            .pipe(
                map(
                    (result: any) =>
                        result.data.getShoppingListCommunicationBatches
                )
            );
    }

    getShoppingListCommunicationRecipients(
        batchId: string,
        skip: number = 0,
        take: number = 10,
        document: string = ''
    ): Observable<any> {
        return this.apollo
            .query({
                query: getShoppingListCommunicationRecipientsQuery,
                variables: {
                    batchId,
                    skip,
                    take,
                    document,
                },
                fetchPolicy: 'no-cache',
            })
            .pipe(
                map(
                    (result: any) =>
                        result.data.getShoppingListCommunicationRecipients
                )
            );
    }

    assignShoppingListToChannels(input: {
        shoppingListIds: string[];
        channelIds: string[];
    }) {
        return this.apollo
            .mutate({
                mutation: assignShoppingListToChannelsMutation,
                variables: input,
            })
            .pipe(
                map((result: any) => result.data.assignShoppingListToChannels)
            );
    }

    removeChannelFromShoppingList(input: {
        shoppingListIds: string[];
        channelIds: string[];
    }) {
        return this.apollo
            .mutate({
                mutation: removeChannelFromShoppingListMutation,
                variables: input,
            })
            .pipe(
                map((result: any) => result.data.removeChannelFromShoppingList)
            );
    }

    downloadTemplateUrl() {
        return this.apollo
            .query({
                query: downloadTemplateUrlQuery,
                fetchPolicy: 'no-cache',
            })
            .pipe(
                map((result: any) => {
                    return result.data.downloadTemplateUrl;
                })
            );
    }

    getCustomerSegments(
        term: string = '',
        skip: number = 0,
        take: number = 20
    ) {
        return this.apollo
            .query({
                query: customerSegmentsQuery,
                variables: {
                    input: {
                        term,
                        skip,
                        take,
                    },
                },
                fetchPolicy: 'no-cache',
            })
            .pipe(map((result: any) => result.data.customerSegments));
    }

    downloadProductsTemplateUrl() {
        return this.apollo
            .query({
                query: downloadProductsTemplateUrlQuery,
                fetchPolicy: 'no-cache',
            })
            .pipe(
                map((result: any) => {
                    return result.data.downloadProductsTemplateUrl;
                })
            );
    }

    getChannelsOptions() {
        return this.apollo
            .query({
                query: getChannelsQuery,
                variables: { skip: 0, take: 1000 },
                fetchPolicy: 'network-only',
            })
            .pipe(
                map((result: any) => {
                    const items = result?.data?.channels?.items ?? [];
                    return items.map((channel: any) => ({
                        id: String(channel.id),
                        code: String(channel.code),
                        label:
                            channel.customFields?.channelName ||
                            channel.customFields?.channelCode ||
                            channel.code,
                    }));
                })
            );
    }
}
